import { TaskDefaultStatusId, TasksSource } from "@jugl-web/rest-api/tasks";
import { isoToLocalDate } from "@jugl-web/utils/date-time";
import { colord } from "colord";
import add from "date-fns/add";
import addDays from "date-fns/addDays";
import differenceInDays from "date-fns/differenceInDays";
import endOfDay from "date-fns/endOfDay";
import getHours from "date-fns/getHours";
import getMinutes from "date-fns/getMinutes";
import getSeconds from "date-fns/getSeconds";
import isAfter from "date-fns/isAfter";
import set from "date-fns/set";
import truncate from "lodash/truncate";
import { TEMPLATE_FOLDER_PRESETS } from "../consts";

// TODO: Divide into separate util files

const MAX_TASK_NAME_LENGTH = 100;

export const getDueDateBasedOnDays = (dueDateInDays: number) =>
  endOfDay(add(new Date(), { days: dueDateInDays }));

export const getDueDateInDays = (dueDate: Date) =>
  differenceInDays(endOfDay(dueDate), endOfDay(new Date()));

export const getDueDateBasedOnDaysAndTime = (date: string, days: number) => {
  const time = isoToLocalDate(date);
  const hours = getHours(time);
  const minutes = getMinutes(time);
  const seconds = getSeconds(time);

  const newDate = addDays(new Date(), days);

  return set(newDate, { hours, minutes, seconds });
};

export interface TransformedActivity {
  custom_fields?: {
    name: string;
    value: string;
    isRemoved?: boolean;
  };
}

export const shortenTaskTitle = (
  title: string,
  maxLength: number = MAX_TASK_NAME_LENGTH
) =>
  truncate(title, {
    length: maxLength,
    omission: "…",
  });

export const isTaskOverdue = <
  TRequiredTaskAttributes extends {
    due_at: string | Date | null;
    status: string;
  },
>(
  task: TRequiredTaskAttributes
) => {
  if (!task.due_at) {
    return false;
  }

  const dueDate =
    task.due_at instanceof Date ? task.due_at : isoToLocalDate(task.due_at);

  return (
    isAfter(new Date(), dueDate) &&
    task.status !== TaskDefaultStatusId.completed
  );
};

export const getRandomFolderPreset = () =>
  TEMPLATE_FOLDER_PRESETS[
    Math.floor(Math.random() * TEMPLATE_FOLDER_PRESETS.length)
  ];

export const getTasksSourceInfo = (source: TasksSource) => {
  const isMyTasks = source.type === "boardTasks" && source.boardId === "my";
  const isTeamTasks = source.type === "boardTasks" && source.boardId === "team";
  const isUserBoardTasks =
    source.type === "boardTasks" && !isMyTasks && !isTeamTasks;
  const isCustomerTasks = source.type === "customerTasks";

  return { isMyTasks, isTeamTasks, isUserBoardTasks, isCustomerTasks };
};

export const extractBoardIdFromTasksSource = (
  source: TasksSource
): string | undefined => {
  const { isUserBoardTasks } = getTasksSourceInfo(source);

  if (source.type === "boardTasks" && isUserBoardTasks) {
    return source.boardId;
  }

  return undefined;
};

export const getBoardFieldColors = (boardColor: string) => {
  const brightness = colord(boardColor).brightness();
  const isDark = brightness < 0.6;

  // If the board color is dark, we need to lighten the background color
  // and darken a bit the text color to make sure it's readable
  if (isDark) {
    return {
      backgroundColor: colord(boardColor)
        // Based on the brightness, we need to lighten more or less
        .lighten(0.85 - brightness)
        .toHex(),
      textColor: colord(boardColor).darken(0.1).toHex(),
    };
  }

  // If the board color is light, we need to darken the text color
  // and put a semi-transparent background color
  return {
    backgroundColor: colord(boardColor).alpha(0.4).toHex(),
    textColor: colord(boardColor).darken(0.8).toHex(),
  };
};
